import { FC, useMemo, useRef, useState } from "react";
import { useServices } from "../entities";
import {
  BinaryAsBooleanValues,
  BrandField,
  ILocation,
  IRegion,
  LanguageKeys,
  VehicleType,
} from "../entities/interface";
import { RadioButton } from "../components/form/RadioButton";
import styles from "./styles.module.css";
import { ServicesList } from "../components/servicesList";
import { useLocation } from "react-router-dom";
import qs from "qs";
import { Spinner } from "../components/spinner";
import logoMDA from "../images/mda.svg";
import logoMTS from "../images/mtr.png";

interface ILocationState {
  locationId: number | null;
  locationName: string;
}

export const PricePage: FC = (props) => {
  let currentLocation = useLocation();
  const querySearchString = currentLocation.search.replace("?", "");
  const params = qs.parse(querySearchString);
  const paramsColour = params.colour;

  useMemo(() => {
    document.documentElement.style.setProperty(
      "--color-main",
      paramsColour ? `#${paramsColour}` : "#f17226"
    );
  }, [paramsColour]);

  const ids = useMemo(
    () => (Array.isArray(params.ids) ? params.ids.map(Number) : []),
    [params.ids]
  );

  const paramsVehicleType = params.vehicleType
    ? params.vehicleType
    : VehicleType.Car;

  const [region, setRegion] = useState<IRegion | undefined>({
    id: params.regionId ? Number(params.regionId) : 0,
    name: { en: "", sw: "" },
    sourceKey: "",
    isAvailable: true,
    locations: [],
    isVisible: true,
  });
  const [location, setLocation] = useState<ILocationState | undefined>({
    locationId: params.locationId ? Number(params.locationId) : null,
    locationName: "",
  });
  const [language, setLanguage] = useState<LanguageKeys | undefined>(
    LanguageKeys.Swedish
  );
  const ref = useRef<HTMLUListElement | null>(null);
  const serviceRef = useRef<HTMLDivElement | null>(null);

  const { data: services, isLoading: servicesLoading } = useServices({
    withAssociationsIds: BinaryAsBooleanValues.True,
    isActive: BinaryAsBooleanValues.True,
    withLocations: BinaryAsBooleanValues.True,
    withRegions: BinaryAsBooleanValues.True,
  });

  const servicesByVehicleType = useMemo(
    () =>
      services?.result.filter(
        (service) => service.vehicleType === paramsVehicleType
      ),
    [services, paramsVehicleType]
  );

  const visibleRegionsByVehicleType = useMemo(
    () =>
      Array.from(
        new Map(
          servicesByVehicleType
            ?.flatMap((ser) => ser.regions)
            .map((reg) => [reg?.id, reg])
        ).values()
      ).filter((reg) => reg?.isVisible),
    [servicesByVehicleType]
  );

  const visibleLocationsByVehicleType = useMemo(
    () =>
      Array.from(
        new Map(
          servicesByVehicleType
            ?.filter(
              (ser) =>
                region?.id &&
                ser.ownLocations.some((loc) => loc.regionId === region?.id)
            )
            .flatMap((ser) => ser.ownLocations)
            .map((location) => [location.id, location])
        ).values()
      ).filter((loc) => loc.isVisible && loc.isActive),
    [region, servicesByVehicleType]
  );

  const handleSetRegion = (reg: IRegion) => {
    setRegion(reg);
    setLocation({ locationId: null, locationName: "" });
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  const handleSetLocation = (loc: ILocation) => {
    setLocation({ locationId: loc?.id, locationName: loc?.name.en });
    if (serviceRef.current) {
      serviceRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSetLanguage = (lng: LanguageKeys) => {
    setLanguage(lng);
    if (serviceRef.current) {
      serviceRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className={styles.container}>
      {servicesLoading ? (
        <Spinner />
      ) : (
        <>
          <h2 className={styles.priceTitle}>Välj stad</h2>
          {visibleRegionsByVehicleType &&
            visibleRegionsByVehicleType?.length > 0 && (
              <ul className={styles.regionContainer}>
                {visibleRegionsByVehicleType?.map((reg) => {
                  if (!reg) return "Region is not find";
                  return (
                    <li key={reg?.id} className={styles.list}>
                      <RadioButton
                        label={reg?.name.sw}
                        selected={region?.id === reg?.id}
                        onSelect={() => handleSetRegion(reg)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
        </>
      )}
      <h2 className={styles.priceTitle}>
        {visibleLocationsByVehicleType &&
        visibleLocationsByVehicleType?.length > 0
          ? "Välj kontor"
          : ""}
      </h2>
      <ul
        className={styles.locationContainer}
        style={{ minHeight: "80px" }}
        ref={ref}
      >
        {
          <>
            {(visibleLocationsByVehicleType || []).map((loc) => {
              if (!loc) return "Location not find";
              return (
                <li key={loc?.id} className={styles.list}>
                  <RadioButton
                    logo={
                      loc.options.brand === BrandField.MDA ? logoMDA : logoMTS
                    }
                    label={loc?.name.sw}
                    selected={location?.locationId === loc?.id}
                    onSelect={() => handleSetLocation(loc)}
                  />
                </li>
              );
            })}
          </>
        }
      </ul>
      <h2 className={styles.priceTitle}>
        {visibleLocationsByVehicleType &&
        visibleLocationsByVehicleType?.length > 0 &&
        location?.locationId
          ? "Välj språk"
          : ""}
      </h2>
      {visibleLocationsByVehicleType &&
        visibleLocationsByVehicleType?.length > 0 &&
        location?.locationId && (
          <ul className={styles.languageContainer} ref={ref}>
            {
              <>
                <li className={styles.list}>
                  <RadioButton
                    label="Svenska"
                    selected={language === LanguageKeys.Swedish}
                    onSelect={() => handleSetLanguage(LanguageKeys.Swedish)}
                  />
                </li>
                <li className={styles.list}>
                  <RadioButton
                    label="Engelska"
                    selected={language === LanguageKeys.English}
                    onSelect={() => handleSetLanguage(LanguageKeys.English)}
                  />
                </li>
              </>
            }
          </ul>
        )}
      <div ref={serviceRef} style={{ minHeight: "300px" }}>
        {location?.locationId && region?.id && (
          <ServicesList
            services={servicesByVehicleType || []}
            servicesLoading={servicesLoading}
            serviceId={params.serviceId?.toString() || null}
            bundleId={params.bundleId?.toString() || null}
            regionId={region?.id || 0}
            locationId={location?.locationId || 0}
            vehicleType={
              params.vehicleType
                ? (params.vehicleType as VehicleType)
                : VehicleType.Car
            }
            language={language || LanguageKeys.Swedish}
            ids={ids}
          />
        )}
      </div>
    </div>
  );
};
